// lib/services/capture/camera_diagnostics.ts
//
// Pure, dependency-free helpers for formatting the WebRTC camera settings /
// capabilities we already read during enumeration, plus a heuristic for
// guessing whether a back camera is an ultra-wide/super-wide lens.
//
// This module deliberately contains NO camera acquisition, NO getUserMedia, and
// NO focus handling. It only formats data the caller already has and computes
// string diagnostics, so it is safe to unit-test in isolation (the capture
// service itself pulls the WebGPU/Stage graph the CommonJS test build can't
// compile).
//
// IMPORTANT: WebRTC exposes no focal length, field-of-view, 35mm-equivalent, or
// physical sensor identity. Ultra-wide detection here is therefore a heuristic
// over the label text and the exposed zoom range — not proof.

// Note emitted once per enumeration so a reader understands the hard limit on
// what the platform can tell us.
export const WEBRTC_LENS_NOTE =
  'WebRTC exposes no focal length, field-of-view, 35mm-equivalent, or sensor ' +
  'identity, so a lens cannot be confirmed as ultra-wide from the API alone; ' +
  'the wide-angle hint below is a label/zoom heuristic, not proof.';

// Safe, imaging-related keys we are willing to surface. Focus keys are
// intentionally excluded — they are never referenced here.
const SETTING_KEYS = [
  'width',
  'height',
  'aspectRatio',
  'frameRate',
  'facingMode',
  'resizeMode',
  'zoom',
  'torch',
  'exposureMode',
  'exposureCompensation',
  'whiteBalanceMode',
  'colorTemperature',
  'brightness',
  'contrast',
  'saturation',
  'sharpness',
  'iso',
] as const;

// Capabilities expose the same imaging fields, generally as ranges or option
// lists rather than scalars.
const CAPABILITY_KEYS = SETTING_KEYS;

// Constraint keys worth echoing back (what we asked the browser for).
const CONSTRAINT_KEYS = [
  'width',
  'height',
  'aspectRatio',
  'frameRate',
  'facingMode',
  'resizeMode',
] as const;

export interface ZoomRange {
  min?: number;
  max?: number;
  step?: number;
}

export interface WideAngleHint {
  // Heuristic verdict. True only when a strong indicator is present; a bare
  // "wide" token alone is treated as inconclusive.
  is_hint: boolean;
  reasons: string[];
}

export interface CameraDiagnostics {
  settings: Record<string, unknown> | null;
  capabilities: Record<string, unknown> | null;
  constraints: Record<string, unknown> | null;
  wide: WideAngleHint;
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function short_id(id: unknown): string {
  if (typeof id !== 'string' || id.length === 0) return '(none)';
  return id.length <= 12 ? id : `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function fmt_scalar(v: unknown): string {
  if (typeof v === 'number') return String(round(v));
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'string') return v;
  return String(v);
}

// Capability values may be a scalar, an array of options, or a
// {min,max,step} range. Render each compactly on a single line.
function fmt_capability(v: unknown): string {
  if (Array.isArray(v)) {
    return `[${v.map((x) => fmt_scalar(x)).join(', ')}]`;
  }
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if ('min' in o || 'max' in o) {
      const min = typeof o.min === 'number' ? round(o.min) : '?';
      const max = typeof o.max === 'number' ? round(o.max) : '?';
      const step =
        typeof o.step === 'number' ? ` step ${round(o.step)}` : '';
      return `[${min}..${max}${step}]`;
    }
    // Unknown object shape — keep it bounded, never dump nested blobs.
    return '[object]';
  }
  return fmt_scalar(v);
}

// One compact "key=value" line per recognized setting. Defensive against a
// null/absent settings object or missing keys.
export function format_settings(
  settings: Record<string, unknown> | null | undefined
): string[] {
  if (!settings) return ['(settings unavailable)'];

  const out: string[] = [];
  for (const key of SETTING_KEYS) {
    if (!(key in settings)) continue;
    const v = settings[key];
    if (v === undefined || v === null) continue;
    out.push(`${key}=${fmt_scalar(v)}`);
  }
  if (typeof settings.deviceId === 'string') {
    out.push(`deviceId=${short_id(settings.deviceId)}`);
  }
  if (typeof settings.groupId === 'string') {
    out.push(`groupId=${short_id(settings.groupId)}`);
  }
  if (out.length === 0) out.push('(no recognized settings present)');
  return out;
}

export function format_capabilities(
  capabilities: Record<string, unknown> | null | undefined
): string[] {
  if (!capabilities) return ['(capabilities not exposed by this browser)'];

  const out: string[] = [];
  for (const key of CAPABILITY_KEYS) {
    if (!(key in capabilities)) continue;
    const v = capabilities[key];
    if (v === undefined || v === null) continue;
    out.push(`${key}=${fmt_capability(v)}`);
  }
  if (out.length === 0) out.push('(no recognized capabilities present)');
  return out;
}

export function format_constraints(
  constraints: Record<string, unknown> | null | undefined
): string[] {
  if (!constraints) return [];
  const out: string[] = [];
  for (const key of CONSTRAINT_KEYS) {
    if (!(key in constraints)) continue;
    const v = constraints[key];
    if (v === undefined || v === null) continue;
    out.push(`${key}=${fmt_capability(v)}`);
  }
  return out;
}

// Pull a {min,max,step} zoom range out of a capabilities object, if present.
export function extract_zoom(
  capabilities: Record<string, unknown> | null | undefined
): ZoomRange | null {
  if (!capabilities || !('zoom' in capabilities)) return null;
  const z = capabilities.zoom;
  if (z && typeof z === 'object') {
    const o = z as Record<string, unknown>;
    const range: ZoomRange = {};
    if (typeof o.min === 'number') range.min = o.min;
    if (typeof o.max === 'number') range.max = o.max;
    if (typeof o.step === 'number') range.step = o.step;
    return range;
  }
  return null;
}

// Strong label indicators of an ultra-wide/super-wide lens. Bare "wide" is
// intentionally NOT here (see below).
const STRONG_WIDE_TOKENS = [
  'ultra wide',
  'ultrawide',
  'ultra-wide',
  'super wide',
  'superwide',
  'super-wide',
  'wide-angle',
  'wide angle',
  '0.5x',
];

// Heuristic: does this look like an ultra-wide lens? Inspects label tokens and
// the exposed zoom range only. Never inspects focus. Returns every reason so
// the log can show the reader why the guess was made.
export function wide_angle_hint(
  label: string,
  zoom?: ZoomRange | null
): WideAngleHint {
  const reasons: string[] = [];
  let is_hint = false;

  const l = (label || '').toLowerCase();

  for (const token of STRONG_WIDE_TOKENS) {
    if (l.includes(token)) {
      reasons.push(`label contains "${token}"`);
      is_hint = true;
    }
  }

  // Some Android devices name the ultra-wide/auxiliary lens "aux".
  if (/\baux\b/.test(l)) {
    reasons.push('label contains "aux" (some devices name the ultra-wide lens this way)');
    is_hint = true;
  }

  // A zoom minimum below 1x means the camera can frame WIDER than the 1x
  // reference lens — a strong ultra-wide signal.
  if (zoom && typeof zoom.min === 'number' && zoom.min < 1) {
    reasons.push(`zoom minimum ${round(zoom.min)} < 1x (can frame wider than 1x)`);
    is_hint = true;
  }

  // A bare "wide" with no ultra/super qualifier is ambiguous: many ordinary
  // rear lenses are simply called "wide". Note it, but do NOT treat it as a
  // positive hint on its own.
  if (!is_hint && /\bwide\b/.test(l)) {
    reasons.push(
      'label contains "wide" but no ultra/super qualifier — ambiguous, treated as inconclusive'
    );
  }

  if (reasons.length === 0) {
    reasons.push('no ultra-wide indicators in label or zoom range');
  }

  return { is_hint, reasons };
}

// Build the diagnostics record for one camera from data already read off its
// track. `settings`/`capabilities`/`constraints` are the raw WebRTC dicts (or
// null when the browser does not expose them).
export function build_diagnostics(
  label: string,
  settings: Record<string, unknown> | null,
  capabilities: Record<string, unknown> | null,
  constraints: Record<string, unknown> | null
): CameraDiagnostics {
  const zoom = extract_zoom(capabilities);
  return {
    settings,
    capabilities,
    constraints,
    wide: wide_angle_hint(label, zoom),
  };
}

// ---- empty-settings detection (Automatic selection aid) ------------------
//
// A camera's stored getSettings() snapshot is "meaningful" when it is a
// non-null object carrying at least one own field with a real value. This is
// used ONLY to avoid Automatic picking a back camera that probed to empty
// settings when a usable back camera exists; it never drives manual override
// and never touches focus.
//
// Semantics:
//   - null / undefined / non-object                        -> empty (false)
//   - {} (zero own fields)                                  -> empty (false)
//   - object whose every own value is undefined/null/""     -> empty (false)
//   - any own value that is 0 or false (valid falsy)         -> meaningful (true)
//   - any browser/vendor-specific own field with a value     -> meaningful (true)
//     (we intentionally do NOT whitelist standard WebRTC keys here)
export function has_meaningful_settings(settings: unknown): boolean {
  if (settings === null || typeof settings !== 'object') return false;
  const obj = settings as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const v = obj[key];
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v === '') continue;
    // 0 and false are preserved as meaningful; any non-empty value counts.
    return true;
  }
  return false;
}

// Minimal shape the back-candidate filter needs: whatever settings snapshot was
// stored for a candidate. Generic so the caller can carry its own payload.
export interface BackCandidateLike {
  settings: unknown;
}

export interface BackFilterResult<T extends BackCandidateLike> {
  // Candidates to select from, in the original deterministic order.
  eligible: T[];
  // Candidates removed by the empty-settings filter (empty when none removed).
  excluded: T[];
  // True iff the empty-settings filter actually removed at least one candidate.
  filtered: boolean;
  // True iff every back candidate had empty settings (>1 candidate case).
  all_empty: boolean;
}

// Apply the Automatic-only empty-settings filter to the back-facing candidates,
// preserving their original order. Rules:
//   - 0 or 1 candidate: unchanged (a lone back stays eligible even if empty).
//   - 2+ candidates, at least one meaningful: drop the empty ones.
//   - 2+ candidates, all empty: keep them all (preserve prior behavior).
// Pure and side-effect-free; never inspects focus.
export function filter_back_candidates<T extends BackCandidateLike>(
  backs: T[]
): BackFilterResult<T> {
  if (backs.length <= 1) {
    const all_empty =
      backs.length === 1 && !has_meaningful_settings(backs[0].settings);
    return { eligible: backs.slice(), excluded: [], filtered: false, all_empty };
  }

  const meaningful = backs.filter((c) => has_meaningful_settings(c.settings));

  if (meaningful.length === 0) {
    return { eligible: backs.slice(), excluded: [], filtered: false, all_empty: true };
  }
  if (meaningful.length === backs.length) {
    return { eligible: backs.slice(), excluded: [], filtered: false, all_empty: false };
  }

  const excluded = backs.filter((c) => !has_meaningful_settings(c.settings));
  return { eligible: meaningful, excluded, filtered: true, all_empty: false };
}

export interface SelectedSummaryInput {
  index: number;
  label: string;
  device_id: string;
  group_id?: string;
  facing: string;
  width: number;
  height: number;
  frame_rate: number;
  aspect?: number | null;
  resize_mode?: string | null;
  zoom?: ZoomRange | null;
  wide: WideAngleHint;
  branch_reason: string;
}

// Concise multi-line summary for the finally-selected camera. Each line is a
// single field or compact group, prefixed for narrow mobile screens.
export function selected_summary(input: SelectedSummaryInput): string[] {
  const aspect =
    typeof input.aspect === 'number' ? round(input.aspect) : 'unknown';
  const resize = input.resize_mode ? input.resize_mode : 'unknown';

  let zoom = 'not exposed';
  if (input.zoom && (input.zoom.min !== undefined || input.zoom.max !== undefined)) {
    const min = input.zoom.min !== undefined ? round(input.zoom.min) : '?';
    const max = input.zoom.max !== undefined ? round(input.zoom.max) : '?';
    zoom = `[${min}..${max}]`;
  }

  const out: string[] = [];
  out.push('--- selected camera summary ---');
  out.push(`index=${input.index}`);
  out.push(`label="${input.label || '(empty)'}"`);
  out.push(`deviceId=${short_id(input.device_id)} groupId=${short_id(input.group_id)}`);
  out.push(`facing=${input.facing} resize=${resize}`);
  out.push(`resolution=${input.width}x${input.height} aspect=${aspect}`);
  out.push(`frameRate=${Math.round(input.frame_rate)}`);
  out.push(`zoom=${zoom}`);
  out.push(
    `wide-angle hint: ${input.wide.is_hint ? 'LIKELY ultra-wide (heuristic)' : 'not indicated (heuristic)'}`
  );
  for (const r of input.wide.reasons) out.push(`  - ${r}`);
  out.push(`selected branch: ${input.branch_reason}`);
  out.push('--- end summary ---');
  return out;
}
